import React, { useState } from "react";
import MiniLoading from "./MiniLoading";
import { useTranslation } from "react-i18next";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate = (d) =>
  new Date(d.endsWith("Z") ? d : d + " UTC").toLocaleString("nl-NL", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

// ─── Type badge ───────────────────────────────────────────────────────────────
const TypeBadge = ({ type, typeMeta }) => {
  const m = typeMeta(type);
  return (
    <span style={{
      display: "inline-block",
      padding: "2px 9px",
      borderRadius: 99,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: ".02em",
      color: m.color,
      background: m.bg,
      border: `1px solid ${m.color}30`,
      whiteSpace: "nowrap",
    }}>
      {m.label}
    </span>
  );
};

// ─── Single row ───────────────────────────────────────────────────────────────
const LogRow = ({ row, typeMeta }) => {
  const [open, setOpen] = useState(false);
  const hasExtras = row.extras && typeof row.extras === "object"
    ? Object.keys(row.extras).length > 0
    : !!row.extras;

  return (
    <>
      <tr
        style={{
          borderBottom: "1px solid #f1f5f9",
          transition: "background .1s",
          cursor: hasExtras ? "pointer" : "default",
        }}
        onMouseEnter={e => (e.currentTarget.style.background = "#f8fafc")}
        onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
        onClick={() => hasExtras && setOpen(o => !o)}
      >
        {/* ID */}
        <td style={{ padding: "11px 16px", fontSize: 12, color: "#94a3b8", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          #{row.id}
        </td>

        {/* Type */}
        <td style={{ padding: "11px 16px", whiteSpace: "nowrap" }}>
          <TypeBadge type={row.type} typeMeta={typeMeta} />
        </td>

        {/* Message */}
        <td style={{ padding: "11px 16px", fontSize: 13, color: "#1e293b", lineHeight: 1.5, wordBreak: "break-word" }}>
          {row.message}
        </td>

        {/* Timestamp */}
        <td style={{ padding: "11px 16px", fontSize: 12, color: "#64748b", whiteSpace: "nowrap" }}>
          {fmtDate(row.created_at)}
        </td>

        {/* Expand toggle */}
        <td style={{ padding: "11px 12px", textAlign: "center", width: 32 }}>
          {hasExtras && (
            <span style={{
              display: "inline-block", width: 18, height: 18, lineHeight: "18px",
              textAlign: "center", borderRadius: 4, fontSize: 11,
              background: open ? "#e0e7ff" : "#f1f5f9",
              color: open ? "#6366f1" : "#94a3b8",
              transition: "transform .15s",
              transform: open ? "rotate(180deg)" : "none",
              userSelect: "none",
            }}>
              ▾
            </span>
          )}
        </td>
      </tr>

      {/* Extras expansion */}
      {open && hasExtras && (
        <tr style={{ background: "#f8fafc" }}>
          <td colSpan={5} style={{ padding: "0 16px 14px 48px" }}>
            <pre style={{
              margin: 0, fontSize: 12, color: "#475569",
              background: "#f1f5f9", borderRadius: 6,
              padding: "10px 14px", overflowX: "auto",
              fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
              lineHeight: 1.6,
            }}>
              {typeof row.extras === "object"
                ? JSON.stringify(row.extras, null, 2)
                : row.extras}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
};

// ─── Pagination ───────────────────────────────────────────────────────────────
const Pager = ({ data, page, onPageChange }) => {
  if (!data || data.last_page <= 1) return null;
  const { current_page, last_page, from, to, total } = data;

  const pages = [];
  const delta = 2;
  for (let i = 1; i <= last_page; i++) {
    if (i === 1 || i === last_page || (i >= current_page - delta && i <= current_page + delta)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== "…") {
      pages.push("…");
    }
  }

  const btn = (label, target, disabled, active = false) => (
    <button
      key={label}
      disabled={disabled}
      onClick={() => !disabled && typeof target === "number" && onPageChange(target)}
      style={{
        minWidth: 32, height: 32, padding: "0 8px",
        border: active ? "1px solid #6366f1" : "1px solid #e2e8f0",
        borderRadius: 6, fontSize: 13, cursor: disabled ? "default" : "pointer",
        background: active ? "#6366f1" : "#fff",
        color: active ? "#fff" : disabled ? "#cbd5e1" : "#374151",
        fontWeight: active ? 600 : 400,
        transition: "all .1s",
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "14px 16px", borderTop: "1px solid #f1f5f9",
      flexWrap: "wrap", gap: 8,
    }}>
      <span style={{ fontSize: 12, color: "#94a3b8" }}>
        {from}–{to} of {total?.toLocaleString()} entries
      </span>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {btn("‹", current_page - 1, current_page === 1)}
        {pages.map((p, i) =>
          p === "…"
            ? <span key={`e${i}`} style={{ padding: "0 4px", color: "#cbd5e1", lineHeight: "32px" }}>…</span>
            : btn(p, p, false, p === current_page)
        )}
        {btn("›", current_page + 1, current_page === last_page)}
      </div>
    </div>
  );
};

// ─── Main table ───────────────────────────────────────────────────────────────
export default function LogsTable({ data, loading, typeMeta, page, onPageChange }) {
  const { t } = useTranslation();

  return (
    <div style={{
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: 10,
      overflow: "hidden",
    }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
            {["#", "Type", "Message", "Timestamp", ""].map((h, i) => (
              <th key={i} style={{
                padding: "10px 16px", textAlign: "left",
                fontSize: 11, fontWeight: 600, color: "#64748b",
                textTransform: "uppercase", letterSpacing: ".05em",
                whiteSpace: "nowrap",
                ...(i === 4 ? { width: 32 } : {}),
              }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={5} style={{ padding: "40px 16px" }}>
                <MiniLoading />
              </td>
            </tr>
          ) : !data || data.data.length === 0 ? (
            <tr>
              <td colSpan={5} style={{ padding: "48px 16px", textAlign: "center", color: "#94a3b8", fontSize: 14 }}>
                {t("No log entries found.")}
              </td>
            </tr>
          ) : (
            data.data.map(row => (
              <LogRow key={row.id} row={row} typeMeta={typeMeta} />
            ))
          )}
        </tbody>
      </table>

      <Pager data={data} page={page} onPageChange={onPageChange} />
    </div>
  );
}
